import { useState } from 'react';

import { Link } from 'react-router-dom';
import { AlertTriangle, GitBranch, Info, Save, ShieldCheck } from 'lucide-react';

import {
  AVISO_DE_ACAO_INDISPONIVEL,
  mostrarAviso,
} from '@/modulos/operacao/comum/cadastroIndisponivel';
import { formatarDataHora } from '@/nucleo/formato/datas';
import { EmptyState } from '@/ui/EmptyState';
import { ErrorState } from '@/ui/ErrorState';
import { PageHeader } from '@/ui/PageHeader';
import { Spinner } from '@/ui/Spinner';
import { Alert, AlertDescription, AlertTitle } from '@/ui/base/alert';
import { Badge } from '@/ui/base/badge';
import { Button } from '@/ui/base/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/ui/base/card';
import { Label } from '@/ui/base/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/ui/base/select';
import { Switch } from '@/ui/base/switch';

import {
  ROTULO_DA_OPERACAO,
  ROTULO_DO_SELO,
  SELOS_GOVBR,
  SIGNIFICADO_DO_SELO,
  useOperacoesGovBr,
  type OperacaoConfigurada,
  type SeloGovBr,
} from './dados';

/**
 * `/admin/parametros/govbr-operacoes` — qual selo gov.br o cidadão precisa ter
 * para **entrar** no sistema, em cada operação.
 *
 * ESTA TELA É SOBRE LOGIN, NÃO SOBRE ASSINATURA. O nível jurídico exigido para
 * assinar um documento mora em `/admin/parametros/politica-assinatura`. São
 * coisas diferentes que se confundem pelo nome, e por isso a tela diz isso logo
 * no primeiro aviso.
 *
 * TAMBÉM NÃO É O CONECTOR gov.br. `/admin/integracoes` parametriza o
 * provedor de identidade de verdade — endereço, cliente, segredo, teste de
 * conexão — e conversa com a API. Aqui é regra de negócio: o selo mínimo.
 *
 * "SALVAR" NÃO GRAVA. `/govbr-operacoes` não existe na API, e a tabela
 * `govbr_operacoes_config` (migração `0002_dominio_base.sql`) não tem política
 * de escrita. O botão só habilita quando há mudança de fato e então mostra o
 * aviso honesto de indisponibilidade
 * (`modulos/operacao/comum/cadastroIndisponivel.ts`).
 */
export function GovbrOperacoesPage() {
  const { data: operacoes, isLoading, isError, error, refetch } = useOperacoesGovBr();

  return (
    <div className="space-y-6">
      <PageHeader
        title="Operações gov.br"
        description="Define qual selo gov.br o cidadão precisa ter para entrar no sistema em cada operação."
      >
        <Button asChild variant="outline" size="sm">
          <Link to="/admin/parametros/fluxo-assinatura">
            <GitBranch className="mr-1.5 h-3.5 w-3.5" aria-hidden="true" />
            Como funciona o fluxo
          </Link>
        </Button>
      </PageHeader>

      <Alert>
        <Info className="h-4 w-4" aria-hidden="true" />
        <AlertTitle>Esta tela controla o acesso, não a assinatura</AlertTitle>
        <AlertDescription className="space-y-1.5 text-xs">
          <div>
            Aqui se define quem pode <strong>entrar</strong>. O nível jurídico exigido para{' '}
            <strong>assinar</strong> um documento fica em{' '}
            <Link to="/admin/parametros/politica-assinatura" className="font-medium underline">
              Política de assinatura digital
            </Link>
            .
          </div>
          <div>
            {SELOS_GOVBR.map((selo, indice) => (
              <span key={selo}>
                {indice > 0 && ' · '}
                <strong>{ROTULO_DO_SELO[selo]}:</strong> {SIGNIFICADO_DO_SELO[selo].toLowerCase()}
              </span>
            ))}
          </div>
        </AlertDescription>
      </Alert>

      {isError ? (
        <ErrorState
          variant="load-failed"
          description={
            error?.aviso ??
            'Não foi possível carregar as operações agora. Verifique sua conexão e tente novamente.'
          }
          primaryAction={{ label: 'Tentar de novo', onClick: () => void refetch() }}
        />
      ) : isLoading ? (
        <Spinner />
      ) : !operacoes || operacoes.length === 0 ? (
        <EmptyState
          icon={ShieldCheck}
          title="Nenhuma operação parametrizada"
          description="Sem parametrização, nenhuma operação exige selo gov.br — o acesso segue o fluxo padrão."
        />
      ) : (
        <div className="grid gap-4 md:grid-cols-2">
          {operacoes.map((operacao) => (
            <CartaoDaOperacao key={operacao.id} operacao={operacao} />
          ))}
        </div>
      )}
    </div>
  );
}

interface CartaoDaOperacaoProps {
  operacao: OperacaoConfigurada;
}

function CartaoDaOperacao({ operacao }: CartaoDaOperacaoProps) {
  const [selo, setSelo] = useState<SeloGovBr>(operacao.selo_minimo);
  const [ativo, setAtivo] = useState(operacao.ativo);

  const alterado = selo !== operacao.selo_minimo || ativo !== operacao.ativo;

  /** Bronze com a exigência ligada deixa entrar quem não conseguirá assinar depois. */
  const permissivoDemais = ativo && selo === 'bronze';

  function salvar() {
    // A tabela `govbr_operacoes_config` não aceita escrita hoje — não há rota na
    // API nem política de escrita no banco. O aviso é honesto: nada foi gravado,
    // e os campos continuam marcados como alterados.
    mostrarAviso(AVISO_DE_ACAO_INDISPONIVEL);
  }

  return (
    <Card>
      <CardHeader className="pb-3">
        <div className="flex items-start justify-between gap-3">
          <div className="min-w-0 space-y-1">
            <CardTitle className="text-base">{ROTULO_DA_OPERACAO[operacao.operacao]}</CardTitle>
            {operacao.descricao && (
              <p className="text-xs text-muted-foreground">{operacao.descricao}</p>
            )}
          </div>
          <Badge variant={ativo ? 'default' : 'outline'} className="shrink-0">
            {ativo ? 'Exige gov.br' : 'Não exige'}
          </Badge>
        </div>
      </CardHeader>
      <CardContent className="space-y-4">
        <div className="grid gap-3 sm:grid-cols-2">
          <div className="space-y-1.5">
            <Label htmlFor={`selo-${operacao.id}`} className="text-xs">
              Selo mínimo
            </Label>
            <Select value={selo} onValueChange={(valor) => setSelo(valor as SeloGovBr)}>
              <SelectTrigger id={`selo-${operacao.id}`}>
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {SELOS_GOVBR.map((item) => (
                  <SelectItem key={item} value={item}>
                    {ROTULO_DO_SELO[item]}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            <p className="text-xs text-muted-foreground">{SIGNIFICADO_DO_SELO[selo]}</p>
          </div>

          <div className="space-y-1.5">
            <Label htmlFor={`ativo-${operacao.id}`} className="text-xs">
              Exigência ativa
            </Label>
            <div className="flex h-10 items-center gap-2 rounded-md border px-3">
              <Switch id={`ativo-${operacao.id}`} checked={ativo} onCheckedChange={setAtivo} />
              <span className="text-xs text-muted-foreground">
                {ativo ? 'Exige gov.br' : 'Segue o fluxo padrão'}
              </span>
            </div>
          </div>
        </div>

        {permissivoDemais && (
          <Alert>
            <AlertTriangle className="h-4 w-4 text-warning" aria-hidden="true" />
            <AlertDescription className="text-xs">
              Configuração permissiva: quem tem selo bronze entra no sistema, mas será recusado ao
              tentar assinar qualquer documento pelo gov.br.
            </AlertDescription>
          </Alert>
        )}

        {operacao.base_legal && (
          <p className="rounded-lg bg-muted/40 px-3 py-2 text-xs text-muted-foreground">
            <strong>Base legal:</strong> {operacao.base_legal}
          </p>
        )}

        <div className="flex flex-wrap items-center justify-between gap-2 border-t pt-3">
          <span className="text-xs text-muted-foreground">
            Atualizado em {formatarDataHora(operacao.atualizado_em)}
          </span>
          <Button size="sm" onClick={salvar} disabled={!alterado}>
            <Save className="mr-1.5 h-3.5 w-3.5" aria-hidden="true" />
            Salvar
          </Button>
        </div>
      </CardContent>
    </Card>
  );
}

export default GovbrOperacoesPage;
